import { CalendarOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader } from 'antd';
import type { Key } from 'react';
import React from 'react';
import { DrawerWidth } from '../../config/dimensions';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { ActionOption, DrawerState, ModuleName } from '../../core/models/enum';
import { isUserAllowed } from '../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../helpers/NotificationHelper';
import { getNewRoute } from '../../helpers/RoutingHelper';
import BatchCompetitionSeasonDrawer from './BatchCompetitionSeasonDrawer';
import { applyBatchSeasonToCompetitions } from './batchCompetitionSeasonApply';
import CompetitionForm from './competition-form';
import CompetitionTable from './competitions-table';
import { Props } from './index';

interface State {
    drawerState: DrawerState;
    isUserAllowedToCreate: boolean;
    batchSelectMode: boolean;
    batchSelectedRowKeys: Key[];
    batchSeasonDrawerVisible: boolean;
}

class Competitions extends React.PureComponent<Props, State> {
    private tableRef: any;

    public constructor(props: Props) {
        super(props);

        const {
            location: { pathname },
        } = window;

        this.tableRef = React.createRef();

        this.state = {
            drawerState:
                pathname === Routes.ROUTE_COMPETITIONS_CREATE
                    ? DrawerState.Edit
                    : DrawerState.Closed,
            isUserAllowedToCreate: false,
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchSeasonDrawerVisible: false,
        };
    }

    public componentDidMount = (): void => {
        this.getPermissions();
    };

    private getPermissions = (): void => {
        const { userProfile } = this.props;

        this.setState({
            isUserAllowedToCreate: isUserAllowed(
                userProfile,
                ModuleName.Competitions,
                ActionOption.Create
            ),
        });
    };

    private handleDrawerMode = (drawerState: DrawerState, id?: string): void => {
        const { history } = this.props;

        this.setState({
            drawerState,
        });

        history.push(
            getNewRoute(
                Routes.ROUTE_COMPETITIONS,
                Routes.ROUTE_COMPETITIONS_READ,
                Routes.ROUTE_COMPETITIONS_CREATE,
                drawerState,
                ReplaceStrings.COMPETITION_ID,
                id
            )
        );
    };

    private updateTable = (): void => {
        this.tableRef.current.getCompetitions();
    };

    private startBatchSeason = (): void => {
        this.setState({
            batchSelectMode: true,
            batchSelectedRowKeys: [],
            batchSeasonDrawerVisible: false,
        });
    };

    private cancelBatchSeason = (): void => {
        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchSeasonDrawerVisible: false,
        });
    };

    private openBatchSeasonDrawer = (): void => {
        if (this.state.batchSelectedRowKeys.length === 0) {
            return;
        }
        this.setState({ batchSeasonDrawerVisible: true });
    };

    private closeBatchSeasonDrawer = (): void => {
        this.setState({ batchSeasonDrawerVisible: false });
    };

    private applyBatchSeason = async (season: string): Promise<void> => {
        const { batchSelectedRowKeys } = this.state;
        const competitionIds = batchSelectedRowKeys.map((k) => Number(k));

        const { ok, failed } = await applyBatchSeasonToCompetitions(competitionIds, season);

        if (failed === 0) {
            success(`Season updated on ${ok} competition(s).`);
        } else if (ok === 0) {
            errorWithUserMessage('Could not update season for any competition.');
        } else {
            success(`Season updated on ${ok} competition(s); ${failed} failed.`);
        }

        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchSeasonDrawerVisible: false,
        });
        this.updateTable();
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const {
            drawerState,
            isUserAllowedToCreate,
            batchSelectMode,
            batchSelectedRowKeys,
            batchSeasonDrawerVisible,
        } = this.state;

        const allowBatchCompetitionUpdate = isUserAllowed(
            userProfile,
            ModuleName.Competitions,
            ActionOption.Update
        );

        const headerExtra: React.ReactElement[] = [];

        if (batchSelectMode) {
            headerExtra.push(
                <Button key="batch-cancel" onClick={this.cancelBatchSeason}>
                    Cancel batch
                </Button>
            );
            headerExtra.push(
                <Button
                    key="batch-next"
                    type="primary"
                    disabled={batchSelectedRowKeys.length === 0}
                    onClick={this.openBatchSeasonDrawer}
                >
                    Next: set season
                </Button>
            );
        } else if (allowBatchCompetitionUpdate) {
            headerExtra.push(
                <Button
                    key="batch-start"
                    icon={<CalendarOutlined />}
                    onClick={this.startBatchSeason}
                >
                    Batch add season
                </Button>
            );
        }

        if (isUserAllowedToCreate) {
            headerExtra.push(
                <Button
                    key="add-competition"
                    type="primary"
                    style={{
                        zIndex: 10,
                        float: 'right',
                    }}
                    onClick={(): void => this.handleDrawerMode(DrawerState.Edit)}
                >
                    <PlusOutlined className="button-icon-display" />
                    Add competition
                </Button>
            );
        }

        return (
            <div>
                <PageHeader
                    title="Competitions"
                    extra={headerExtra.length ? headerExtra : undefined}
                    style={{ padding: '16px 0px' }}
                />

                {batchSelectMode && (
                    <div style={{ marginBottom: 12, color: '#595959' }}>
                        Step 1: tick the competitions to update, then click &quot;Next: set
                        season&quot;.
                    </div>
                )}

                <CompetitionTable
                    userProfile={userProfile}
                    wrappedComponentRef={this.tableRef}
                    batchSelectionMode={batchSelectMode}
                    batchSelectedRowKeys={batchSelectedRowKeys}
                    onBatchSelectedRowKeysChange={(keys: Key[]): void =>
                        this.setState({ batchSelectedRowKeys: keys })
                    }
                />

                <Drawer
                    title="Batch add season — step 2"
                    visible={batchSeasonDrawerVisible}
                    onClose={this.closeBatchSeasonDrawer}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    <BatchCompetitionSeasonDrawer
                        visible={batchSeasonDrawerVisible}
                        selectedCompetitionCount={batchSelectedRowKeys.length}
                        onClose={this.closeBatchSeasonDrawer}
                        onApply={this.applyBatchSeason}
                    />
                </Drawer>

                <Drawer
                    title="Add federation"
                    visible={!!drawerState}
                    onClose={(): void => this.handleDrawerMode(DrawerState.Closed)}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    <CompetitionForm
                        handleClose={this.handleDrawerMode}
                        refreshAfterSave={this.updateTable}
                    />
                </Drawer>
            </div>
        );
    }
}

export default Competitions;
